import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import type { Asset } from '@frameio/graphql-client/generated';
import { getLink, useCopyLink } from '@frameio/shared/routing';
import { openInNewTab } from '../../utils';
import { STANDARD_RESULT_SOURCE } from '../constants';
import {
  useQuickSearchDownloadAssets,
  useQuickSearchModal,
  useSearchHistoryData,
  useTrackActionedOnResults,
} from '../contexts';
import type {
  TrackResultSource,
  TrackResultType,
  TrackSearchFilter,
} from '../contexts/SearchAnalyticsContext/types';
import type { SectionId } from '../types';
import { getAssetResultItemLink, getMatchTypeTrackingValues } from '../utils';
import { useQuickSearchAssetData } from './useQuickSearchAssetData';

export type UseAssetActionsParams = {
  appliedFilterTypes: TrackSearchFilter;
  assetId: string;
  isAdvancedSearch?: boolean;
  resultPosition: number;
  resultSource?: TrackResultSource;
  sectionId?: SectionId;
  searchMode?: SectionId;
  searchQuery?: string;
};

export const useAssetActions = ({
  appliedFilterTypes,
  assetId,
  isAdvancedSearch = false,
  resultPosition,
  resultSource = STANDARD_RESULT_SOURCE,
  sectionId,
  searchMode = sectionId,
  searchQuery,
}: UseAssetActionsParams) => {
  const router = useRouter();
  const copyLink = useCopyLink();
  const { closeModal } = useQuickSearchModal();
  const { downloadAssets } = useQuickSearchDownloadAssets();
  const trackActionedOnResults = useTrackActionedOnResults();
  const { addRecentSearch } = useSearchHistoryData();

  const { assetData, matches } = useQuickSearchAssetData({
    assetId,
    sectionId: searchMode,
  });

  const matchType = useMemo(
    () => getMatchTypeTrackingValues(matches?.nodes ?? []),
    [matches?.nodes]
  );

  const isVersionStack = Number.isInteger(assetData?.versionNumber);
  const project = assetData?.project;
  const isRecentlyDeleted = Boolean(assetData?.deletedAt);

  const parentFolder = isVersionStack
    ? assetData?.parent?.ancestorFolders?.at(-1)
    : assetData?.parent;

  const canDownloadAsset = project?.permissions?.canDownloadAsset ?? false;
  const isFolderAsset = assetData?.__typename === 'FolderAsset';
  const resultType: TrackResultType = isFolderAsset ? 'Folder' : 'Asset';
  const webSource = isAdvancedSearch ? 'FullScreen' : 'Modal';

  const recordSearchHistory = useCallback(() => {
    if (!searchMode || !searchQuery) return;

    addRecentSearch({
      query: searchQuery,
      mode: searchMode,
    });
  }, [addRecentSearch, searchQuery, searchMode]);

  const handleDownload = useCallback(() => {
    recordSearchHistory();
    trackActionedOnResults?.({
      action: 'Download',
      assetCount: isFolderAsset ? undefined : 1,
      filterApplied: appliedFilterTypes,
      folderCount: isFolderAsset ? 1 : undefined,
      isBulkOperation: false,
      matchType,
      resultPosition,
      resultSource,
      resultType,
      webSource,
    });
    downloadAssets(assetId);
  }, [
    appliedFilterTypes,
    assetId,
    downloadAssets,
    isFolderAsset,
    matchType,
    recordSearchHistory,
    resultPosition,
    resultSource,
    resultType,
    trackActionedOnResults,
    webSource,
  ]);

  const handleCopyLink = useCallback(() => {
    recordSearchHistory();
    trackActionedOnResults?.({
      action: 'CopyUrl',
      filterApplied: appliedFilterTypes,
      matchType,
      resultPosition,
      resultSource,
      resultType,
      webSource,
    });
    const href = getAssetResultItemLink(assetData as Asset, {
      isRecentlyDeleted,
    });
    copyLink(href);
  }, [
    appliedFilterTypes,
    assetData,
    copyLink,
    isRecentlyDeleted,
    matchType,
    recordSearchHistory,
    resultPosition,
    resultSource,
    resultType,
    trackActionedOnResults,
    webSource,
  ]);

  const handleViewInProject = useCallback(() => {
    const projectId = project?.id;
    if (!projectId) {
      return;
    }
    recordSearchHistory();
    trackActionedOnResults?.({
      action: 'OpenInSource',
      filterApplied: appliedFilterTypes,
      matchType,
      resultPosition,
      resultSource,
      resultType,
      webSource,
    });
    const href = getLink({
      page: 'folder',
      projectId,
      folderAssetId: parentFolder?.id ?? '',
      assetId,
    });

    router.push(href);
    closeModal({ navigatingTo: href });
  }, [
    appliedFilterTypes,
    assetId,
    closeModal,
    matchType,
    parentFolder?.id,
    project?.id,
    recordSearchHistory,
    resultPosition,
    resultSource,
    resultType,
    router,
    trackActionedOnResults,
    webSource,
  ]);

  const handleOpenInNewTab = useCallback(() => {
    recordSearchHistory();
    trackActionedOnResults?.({
      action: 'OpenInNewTab',
      filterApplied: appliedFilterTypes,
      matchType,
      resultPosition,
      resultSource,
      resultType,
      webSource,
    });
    const href = getAssetResultItemLink(assetData as Asset, {
      isRecentlyDeleted,
    });
    openInNewTab(href);
  }, [
    appliedFilterTypes,
    assetData,
    isRecentlyDeleted,
    matchType,
    recordSearchHistory,
    resultPosition,
    resultSource,
    resultType,
    trackActionedOnResults,
    webSource,
  ]);

  const handleViewInTrash = useCallback(() => {
    const href = getAssetResultItemLink(assetData as Asset, {
      isRecentlyDeleted,
    });
    recordSearchHistory();
    router.push(href);
    closeModal({ navigatingTo: href });
  }, [assetData, closeModal, isRecentlyDeleted, recordSearchHistory, router]);

  return {
    canDownloadAsset,
    handleCopyLink,
    handleDownload,
    handleOpenInNewTab,
    handleViewInProject,
    handleViewInTrash,
    isRecentlyDeleted,
    matchType,
    projectId: project?.id,
    resultType,
  };
};
